// src/components/DeleteAccountModal.tsx
// Confirmation modal for the "Delete my account" flow.
// Honest about scope: permanent removal after 7 days, payment records retained
// per law with personal details stripped, third-party providers age out separately.

import { useState } from 'react';

const BRAND_CORAL = '#E05A3A';
const BRAND_INK   = '#1A1A1A';

const REQUIRED_PHRASE = 'delete account permanently';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (confirmationPhrase: string) => Promise<void>;
}

export default function DeleteAccountModal({ isOpen, onClose, onConfirm }: Props) {
  const [phrase, setPhrase]     = useState('');
  const [busy, setBusy]         = useState(false);
  const [error, setError]       = useState('');

  if (!isOpen) return null;

  const matches = phrase.trim().toLowerCase() === REQUIRED_PHRASE;

  async function handleConfirm() {
    if (!matches || busy) return;
    setBusy(true);
    setError('');
    try {
      await onConfirm(phrase.trim());
    } catch (e: any) {
      setError(e?.message || 'Something went wrong. Please try again.');
      setBusy(false);
    }
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="delete-modal-title"
      style={{
        position: 'fixed', inset: 0, zIndex: 9999,
        background: 'rgba(0,0,0,0.55)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: '16px',
      }}
      onClick={(e) => { if (e.target === e.currentTarget && !busy) onClose(); }}
    >
      <div style={{
        background: '#fff',
        borderRadius: 20,
        border: `3px solid ${BRAND_INK}`,
        boxShadow: `6px 6px 0px ${BRAND_INK}`,
        padding: '28px 24px',
        maxWidth: 440,
        width: '100%',
      }}>
        <h2
          id="delete-modal-title"
          style={{
            fontFamily: "'Fredoka', sans-serif",
            fontSize: 22,
            fontWeight: 600,
            color: BRAND_INK,
            marginBottom: 16,
          }}
        >
          Delete your account?
        </h2>

        <div style={{
          fontFamily: "'Nunito', sans-serif",
          fontSize: 14,
          color: '#333',
          lineHeight: 1.6,
          marginBottom: 20,
          display: 'flex',
          flexDirection: 'column',
          gap: 10,
        }}>
          <p>
            Your account will be <strong>locked immediately</strong> and all your data
            removed from Crushhh after <strong>7 days</strong>. You can cancel within that
            window by logging back in.
          </p>
          <p>
            <strong>What's removed:</strong> your profile, crush list, contact points, push
            tokens, and match history.
          </p>
          <p>
            <strong>What stays:</strong> payment records are kept as required by law, with
            your personal details stripped. Backups and third-party providers (payment
            processor, SMS provider) age out on their own schedules — we cannot accelerate
            that on your behalf.
          </p>
        </div>

        <label
          htmlFor="delete-phrase-input"
          style={{
            display: 'block',
            fontFamily: "'Nunito', sans-serif",
            fontSize: 13,
            fontWeight: 700,
            color: BRAND_INK,
            marginBottom: 6,
          }}
        >
          Type <em>delete account permanently</em> to confirm:
        </label>
        <input
          id="delete-phrase-input"
          type="text"
          value={phrase}
          onChange={e => { setPhrase(e.target.value); setError(''); }}
          disabled={busy}
          autoComplete="off"
          spellCheck={false}
          placeholder="delete account permanently"
          style={{
            width: '100%',
            boxSizing: 'border-box',
            border: `2px solid ${matches ? BRAND_CORAL : '#ccc'}`,
            borderRadius: 10,
            padding: '10px 12px',
            fontFamily: "'Nunito', sans-serif",
            fontSize: 14,
            color: BRAND_INK,
            outline: 'none',
            marginBottom: 16,
            transition: 'border-color 0.15s',
          }}
        />

        {error && (
          <p style={{
            fontFamily: "'Nunito', sans-serif",
            fontSize: 13,
            color: BRAND_CORAL,
            marginBottom: 12,
          }}>
            {error}
          </p>
        )}

        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
          <button
            onClick={onClose}
            disabled={busy}
            style={{
              fontFamily: "'Nunito', sans-serif",
              fontSize: 14,
              fontWeight: 700,
              padding: '10px 20px',
              borderRadius: 100,
              border: `2px solid ${BRAND_INK}`,
              background: '#fff',
              color: BRAND_INK,
              cursor: busy ? 'default' : 'pointer',
              opacity: busy ? 0.5 : 1,
            }}
          >
            Cancel
          </button>
          <button
            onClick={handleConfirm}
            disabled={!matches || busy}
            style={{
              fontFamily: "'Nunito', sans-serif",
              fontSize: 14,
              fontWeight: 700,
              padding: '10px 20px',
              borderRadius: 100,
              border: 'none',
              background: matches && !busy ? BRAND_CORAL : '#ccc',
              color: '#fff',
              cursor: matches && !busy ? 'pointer' : 'not-allowed',
              transition: 'background 0.15s',
            }}
          >
            {busy ? 'Deleting…' : 'Delete my account'}
          </button>
        </div>
      </div>
    </div>
  );
}
